import { FileExtensions } from '@crew/enums/app';
/**
 * 引数のbase64エンコード文字列からblobに変換する関数
 * @param base64 base64エンコード文字列
 * @returns
 */
export const base64ToBlob = (base64) => {
    // base64データのファイルタイプを抜き出す
    const fileType = base64.slice(base64.indexOf(':') + 1, base64.indexOf(';'));
    var bin = window.atob(base64.replace(/^.*,/, ''));
    var buffer = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
    }
    // Blobを作成
    try {
        var blob = new Blob([buffer.buffer], {
            type: fileType,
        });
    }
    catch (e) {
        console.warn('Failed to create Blob. ' + e);
        return;
    }
    return blob;
};
/**
 * convertToByte
 * @date 6/16/2023 - 2:02:57 PM
 *
 * @param {number} value
 * @param {('B' | 'KB' | 'MB' | 'GB')} [inputUnit='MB']
 * @returns {number}
 */
export const convertToByte = (value, inputUnit // bytes, kilobytes, megabytes, gigabytes
) => {
    switch (inputUnit) {
        case 'B':
            return value;
        case 'KB':
            return value * Math.pow(1024, 1);
        case 'MB':
            return value * Math.pow(1024, 2);
        case 'GB':
            return value * Math.pow(1024, 3);
        default:
            return value;
    }
};
/**
 * 引数のblobエンコード文字列からbase64に変換する関数
 * @param blob blobエンコード文字列
 * @returns
 */
export const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, _) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
};
// react-doc-viewer Supported file types
// https://github.com/cyntler/react-doc-viewer#supported-file-types
export const PREVIEW_SUPPORTED_EXTENSIONS_BY_REACT_DOC_VIEWER = [
    FileExtensions.Bmp,
    FileExtensions.Csv,
    FileExtensions.Odt,
    FileExtensions.Gif,
    FileExtensions.Htm,
    FileExtensions.Html,
    FileExtensions.Jpg,
    FileExtensions.Jpeg,
    FileExtensions.Pdf,
    FileExtensions.Png,
    FileExtensions.Tiff,
    FileExtensions.Txt,
    FileExtensions.Doc,
    FileExtensions.Docx,
    FileExtensions.Xls,
    FileExtensions.Xlsx,
    FileExtensions.Ppt,
    FileExtensions.Pptx,
    FileExtensions.Mp4,
];
// video support extensions by light box
export const PREVIEW_SUPPORTED_VIDEO_EXTENSIONS = [
    FileExtensions.Mp4,
    FileExtensions.Avi,
    FileExtensions.Webm,
    FileExtensions.Mov,
];
// 音声ファイルでプレビューが可能な拡張子一覧
export const PREVIEW_SUPPORTED_AUDIO_EXTENSIONS = [
    FileExtensions.Mp3,
    FileExtensions.Wav,
    FileExtensions.Oga,
    FileExtensions.M4a,
];
// 再生可能な動画ファイルのMimeType
export const PreviewSupportedVideoMimeType = {
    Mp4: 'video/mp4',
    Avi: 'video/x-msvideo',
    Webm: 'video/webm',
    Mov: 'video/mp4', // Lightboxで指定した際、videoタグで扱うにあたり、ブラウザの互換性上、video/quicktimeだと動作しないため、video/mp4として扱う
};
// 再生可能な音声ファイルのMimeType
export const PreviewSupportedAudioMimeType = {
    Mp3: 'audio/mpeg',
    Wav: 'audio/wav',
    Oga: 'audio/ogg',
    M4a: 'audio/mp4',
};
// generate file type from file extension
export const getFileType = (fileExtension) => {
    switch (fileExtension === null || fileExtension === void 0 ? void 0 : fileExtension.toLocaleLowerCase()) {
        // For the office file, the type of preview file is Pdf
        case FileExtensions.Pdf:
        case FileExtensions.Doc:
        case FileExtensions.Docx:
        case FileExtensions.Xls:
        case FileExtensions.Xlsx:
        case FileExtensions.Ppt:
        case FileExtensions.Pptx:
            return 'application/pdf';
        case FileExtensions.Txt:
            return 'text/plain';
        case FileExtensions.Tiff:
            return 'image/tiff';
        case FileExtensions.Csv:
            return 'text/csv';
        case FileExtensions.Gif:
            return 'image/gif';
        case FileExtensions.Htm:
            return 'text/htm';
        case FileExtensions.Html:
            return 'text/html';
        case FileExtensions.Png:
            return 'image/png';
        case FileExtensions.Jpg:
            return 'image/jpg';
        case FileExtensions.Jpeg:
            return 'image/jpeg';
        case FileExtensions.Bmp:
            return 'image/bmp';
        case FileExtensions.Odt:
            return 'application/vnd.oasis.opendocument.text';
        case FileExtensions.Mp4:
            return 'video/mp4';
        case FileExtensions.Avi:
            return 'video/x-msvideo';
        case FileExtensions.Webm:
            return 'video/webm';
        case FileExtensions.Mov:
            return 'video/mp4'; // Lightboxで指定した際、videoタグで扱うにあたり、ブラウザの互換性上、video/quicktimeだと動作しないため、video/mp4として扱う
        case FileExtensions.Mp3:
            return 'audio/mpeg';
        case FileExtensions.Wav:
            return 'audio/wav';
        case FileExtensions.Oga:
            return 'audio/ogg';
        case FileExtensions.M4a:
            return 'audio/mp4';
        default:
            return 'application/octet-stream';
    }
};
// image package of backend only supports 3 formats jpeg, gif, and png
// https://pkg.go.dev/image#section-directories
export const MEETING_BACKGROUND_IMAGE_VALID_EXTENSIONS = [
    FileExtensions.Jpeg,
    FileExtensions.Jpg,
    FileExtensions.Png,
    FileExtensions.Gif,
];
// Check background image file support valid extensions
export const isValidMeetingBackgroundFile = (fileName) => {
    const fileExtension = fileName.toLowerCase().split('.').pop();
    return (MEETING_BACKGROUND_IMAGE_VALID_EXTENSIONS.findIndex((extensionItem) => extensionItem === fileExtension) > -1);
};
// プレビュー可能なファイルかをチェックする
// 可能なのは、react-doc-viewer対応のファイルと、light boxで再生可能な動画・音声ファイル
export const isValidPreviewFile = (fileName) => {
    const fileExtension = fileName.toLowerCase().split('.').pop();
    return (PREVIEW_SUPPORTED_EXTENSIONS_BY_REACT_DOC_VIEWER.findIndex((extensionItem) => extensionItem === fileExtension) > -1 ||
        PREVIEW_SUPPORTED_VIDEO_EXTENSIONS.findIndex((extensionItem) => extensionItem === fileExtension) > -1 ||
        PREVIEW_SUPPORTED_AUDIO_EXTENSIONS.findIndex((extensionItem) => extensionItem === fileExtension) > -1);
};
// List thumbnail support valid extensions
export const THUMBNAIL_SUPPORT_EXTENSIONS = [
    FileExtensions.Mp4,
    FileExtensions.Webm,
    FileExtensions.Ogg,
    FileExtensions.Pdf,
    FileExtensions.Svg,
    FileExtensions.Png,
    FileExtensions.Jpg,
    FileExtensions.Jpeg,
    FileExtensions.Gif,
    FileExtensions.Bmp,
    FileExtensions.Tiff,
    FileExtensions.Tif,
    FileExtensions.Doc,
    FileExtensions.Docx,
    FileExtensions.Xls,
    FileExtensions.Xlsx,
    FileExtensions.Ppt,
    FileExtensions.Pptx,
];
// Check thumbnail support valid extensions
export const isValidThumbnailFile = (fileName) => {
    const fileExtension = fileName.toLowerCase().split('.').pop();
    return (THUMBNAIL_SUPPORT_EXTENSIONS.findIndex((extensionItem) => extensionItem === fileExtension) > -1);
};
// The extension list of office file
export const OFFICE_FILE_EXTENSIONS = [
    FileExtensions.Doc,
    FileExtensions.Docx,
    FileExtensions.Xls,
    FileExtensions.Xlsx,
    FileExtensions.Ppt,
    FileExtensions.Pptx,
];
// Check for valid office file
export const isOfficeFile = (fileName) => {
    const fileExtension = fileName.toLowerCase().split('.').pop();
    return (OFFICE_FILE_EXTENSIONS.findIndex((extensionItem) => extensionItem === fileExtension) > -1);
};
// check for valid preview video
export const isValidPreviewVideo = (fileName) => {
    const fileExtension = fileName.toLowerCase().split('.').pop();
    return (PREVIEW_SUPPORTED_VIDEO_EXTENSIONS.findIndex((extensionItem) => extensionItem === fileExtension) > -1);
};
// プレビュー可能な音声ファイルかをチェックする
export const isValidPreviewAudio = (fileName) => {
    const fileExtension = fileName.toLowerCase().split('.').pop();
    return (PREVIEW_SUPPORTED_AUDIO_EXTENSIONS.findIndex((extensionItem) => extensionItem === fileExtension) > -1);
};
// ストリーム再生可能なファイルかどうかを判定する
export const isStreamableFile = (fileName) => {
    var _a;
    const extension = (_a = fileName.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    // 対応拡張子の場合は再生可能
    switch (extension) {
        case FileExtensions.Mp4:
        case FileExtensions.Mp3:
        case FileExtensions.Wav:
        case FileExtensions.Oga:
        case FileExtensions.M4a:
            return true;
    }
    // それ以外は再生不可
    return false;
};
